import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastModule } from 'primeng/toast';
import { TreeModule } from 'primeng/tree';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import {
  DynamicDialogModule,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';

import { buildCategoryTree, practiceOfCategoryKey, resolveSelection } from '../../../../core/utils/category-tree';
import { CareerPathService } from '../../../../core/services/career-path.service';
import { CategoryMappingService } from '../../../../core/services/category-mapping.service';
import { PoolFilterService } from '../../../../core/services/pool-filter.service';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';

@Component({
  selector: 'em-pool-form',
  standalone: true,
  imports: [
    ToastModule,
    TreeModule,
    DynamicDialogModule,
    CommonModule,
    FormsModule,
    CheckboxModule,
    ButtonModule,
    TranslatePipe,
  ],
  templateUrl: './pool-form.component.html',
  styleUrl: './pool-form.component.scss',
})
export class PoolFormComponent {
  careerPaths!: any;
  selected: any[] = [];
  translations: any = {};
  /**
   * Practice a Manager / Manager People is confined to. Null for everyone else.
   * When set, the tree shows only that practice and the saved selection is merged
   * back onto the untouched entries of the other practices.
   */
  private readonly scopedPractice: string | null;

  /**
   * How many actual categories are selected.
   *
   * PrimeNG adds fully selected parents to the selection alongside their
   * children, so a plain length would count practices and career paths too.
   * Only leaves carry shortLabel.
   */
  get selectedCategoryCount(): number {
    return this.selected.filter((node) => node?.shortLabel).length;
  }

  constructor(
    private careerPathService: CareerPathService,
    private categoryService: CategoryMappingService,
    private poolFilterService: PoolFilterService,
    private translationService: TranslateService,
    public ref: DynamicDialogRef,
    config: DynamicDialogConfig,
  ) {
    this.scopedPractice = (config?.data?.scopedPractice ?? null) as string | null;
  }

  ngOnInit(): void {
    // The tree and the saved selection are loaded together: the selection has to
    // be resolved against the finished tree, and as two separate subscriptions
    // whichever finished last used to win.
    this.loadCareerPathsAndSelection();
    this.loadTranslations();
  }

  private loadTranslations(): void {
    this.translationService
      .get(['layout.configurations.pool.addPoolFilter.save'])
      .subscribe((response) => {
        this.translations = response;
      });
  }

  private loadCareerPathsAndSelection(): void {
    // Practice -> Career Path -> Category. This used to put career paths at the
    // top level, so entries from unrelated practices sat side by side with no
    // indication of which practice they belonged to. Shared with the evaluation
    // filter, which had the same list built the same way.
    forkJoin({
      careerPaths: this.careerPathService.getAll(),
      categories: this.categoryService.getAll(),
      savedFilter: this.poolFilterService.getPoolFilter(),
    }).subscribe(({ careerPaths, categories, savedFilter }) => {
      // A scoped user only ever sees their own practice, so a practice filter is
      // neither shown nor needed here.
      const scopedCareerPaths = this.scopedPractice === null
        ? careerPaths
        : (careerPaths ?? []).filter((cp: any) => this.isScopedPractice(cp?.practice));
      const scopedCategories = this.scopedPractice === null
        ? categories
        : (categories ?? []).filter((c: any) => this.isScopedPractice(c?.careerPath?.practice));

      this.careerPaths = buildCategoryTree(scopedCareerPaths, scopedCategories);

      if (!savedFilter?.filterJson) {
        return;
      }

      // Resolve to the real tree nodes. Rebuilding the selection as plain objects
      // meant PrimeNG, which matches by object identity, never recognised them -
      // so restored ticks did not count towards their parent.
      const savedKeys = (JSON.parse(savedFilter.filterJson) as Array<{ key: string }>)
        .map((item) => item.key);
      this.selected = resolveSelection(this.careerPaths, savedKeys);
    });
  }

  private isScopedPractice(practice: string | null | undefined): boolean {
    return this.scopedPractice !== null
      && (practice ?? '').trim().toLowerCase() === this.scopedPractice.toLowerCase();
  }
public submitData(): void {
  // Only category leaves carry these three properties, so this also filters out
  // the Practice and Career Path branches that the tree now adds above them.
  const selectedLeafNodes = this.selected.filter(
    (selected) => selected?.practice && selected?.careerPath && selected?.shortLabel
  );

  const selection = selectedLeafNodes.map((selected) => ({
    key: selected.key,
    label: selected.label,
    practice: selected.practice,
    careerPath: selected.careerPath,
    shortLabel: selected.shortLabel,
    division: selected.division ?? null,
  }));

  if (this.scopedPractice === null) {
    const filter = JSON.stringify(selection);
    this.poolFilterService.updatePoolFilter(filter).subscribe(() => {
      this.ref.close({ filters: selection, saved: true });
    });
    return;
  }

  // A scoped user owns only their practice: merge their selection with the other
  // practices' entries that the dialog never showed, so saving here cannot drop
  // another practice's configuration.
  this.poolFilterService.getPoolFilter().subscribe((response) => {
    const existing = this.parseExisting(response?.filterJson);
    const others = existing.filter((entry) => !this.isScopedPractice(entry?.practice ?? practiceOfCategoryKey(entry?.key)));
    const merged = [...others, ...selection];

    this.poolFilterService.updatePoolFilter(JSON.stringify(merged)).subscribe(() => {
      this.ref.close({ filters: merged, saved: true });
    });
  });
}

private parseExisting(filterJson: string | null | undefined): any[] {
  try {
    const parsed = JSON.parse(filterJson || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

}